import { ActionsObservable, StateObservable } from 'redux-observable';
import { Action, ActionCreator } from '@reduxjs/toolkit';
import { of } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  concatMap,
} from 'rxjs/operators';

import { push } from 'connected-react-router';
import * as api from 'api/processDefinition';
import * as taskApi from 'api/taskManagement';
import * as formApi from 'api/formInteraction';
import { processDefinitionFormatter } from 'utils/processDefinition';
import {
  permissionNotificationErrorProps,
  processDefinitionFormNotFoundErrorProps,
  processDefinitionNotFoundErrorProps,
  tryAgainNotificationErrorProps,
} from 'constants/errorProps';
import { ROUTES, TAB_ROUTES } from 'constants/routes';
import { RootState } from 'store/rootReducer';
import { formatFormSubmissionToTaskData, getValidationErrors } from '#shared/utils/formData';
import { ErrorInfo } from '#shared/types/common';
import { ProcessDefinition } from '#shared/types/processDefinition';
import { ALL_TASKS_FILTER_PARAMS } from 'constants/tableFiltering';
import i18n from 'localization';
import {
  getCriticalErrorProps,
  isValidationError,
  catchError,
  isPermissionError,
  getComponentErrorProps,
  getNotificationErrorProps,
} from '#shared/utils/apiHelpers';

import {
  getProcessDefinitionsRequest,
  getProcessDefinitionsSuccess,
  getProcessDefinitionsError,
  startProcessRequest,
  startProcessSuccess,
  startProcessError,
  startProcessWithFormRequest,
  startProcessWithFormSuccess,
  startProcessWithFormError,
  getProcessDefinitionFormRequest,
  getProcessDefinitionSuccess,
  getProcessDefinitionFormSuccess,
  getProcessDefinitionFormError,
  getProcessDefinitionError,
  getGroupedProcessDefinitionsError,
  getGroupedProcessDefinitionsRequest,
  getGroupedProcessDefinitionsSuccess,
} from './slice';
import { selectProcessDefinitionForm } from './selectors';

// helper for choose action after process is started
function processStartPipe(
  startResult: { ended: boolean, id: string },
  startProcessAction: ActionCreator<{ type: string }>,
) {
  const getResultObservable = (route: string) => of(startProcessAction(), push(route, { forceLeave: true }));
  if (startResult.ended) {
    return getResultObservable(TAB_ROUTES.PROCESS_INSTANCE_LIST_ENDED);
  }

  return taskApi.getPendingTaskIdsList({ ...ALL_TASKS_FILTER_PARAMS, maxResults: 1 }, startResult.id).pipe(
    mergeMap(({ response: initiatedTasks }) => {
      if (initiatedTasks.length) {
        const taskId = initiatedTasks[0].id;
        return getResultObservable(ROUTES.USER_TASK.replace(':taskId', taskId));
      }

      return getResultObservable(TAB_ROUTES.PROCESS_INSTANCE_LIST_ACTIVE);
    }),
    catchError(() => getResultObservable(ROUTES.PROCESS_INSTANCE_LIST)),
  );
}

export const getProcessDefinitionsEpic = (
  action$: ActionsObservable<Action>,
) => {
  return action$
    .pipe(
      filter(getProcessDefinitionsRequest.match),
      mergeMap(() => {
        return api.getProcessListDefinitions().pipe(
          map((response) => {
            const formattedProcessDefinitionList = processDefinitionFormatter(response.response);
            return getProcessDefinitionsSuccess(formattedProcessDefinitionList);
          }),
          catchError((serverResponse) => of(
            getProcessDefinitionsError(getCriticalErrorProps({ serverResponse })),
          )),
        );
      }),
    );
};

export const getGroupedProcessDefinitionsEpic = (
  action$: ActionsObservable<Action>,
) => {
  return action$
    .pipe(
      filter(getGroupedProcessDefinitionsRequest.match),
      mergeMap(() => {
        return api.getGroupedProcessListDefinitions().pipe(
          map((response) => {
            return getGroupedProcessDefinitionsSuccess(response.response);
          }),
          catchError((serverResponse) => of(
            getGroupedProcessDefinitionsError(getComponentErrorProps({ serverResponse })),
          )),
        );
      }),
    );
};

export const startProcessEpic = (
  action$: ActionsObservable<Action>,
) => {
  return action$
    .pipe(
      filter(startProcessRequest.match),
      mergeMap(({ payload }) => {
        return api.startProcess(payload).pipe(
          mergeMap(({ response }) => processStartPipe(response, startProcessSuccess)),
          catchError(({ response }) => of(startProcessError(
            getNotificationErrorProps(response, tryAgainNotificationErrorProps),
          ))),
        );
      }),
    );
};

export const startProcessWithFormEpic = (
  action$: ActionsObservable<Action>,
  state$: StateObservable<RootState>,
) => {
  return action$
    .pipe(
      filter(startProcessWithFormRequest.match),
      mergeMap(({ payload }) => {
        return api.startProcessWithForm(payload.processDefinitionKey, formatFormSubmissionToTaskData(payload.formData))
          .pipe(
            mergeMap(({ response }) => {
              return processStartPipe(response, startProcessWithFormSuccess);
            }),
            catchError((response) => {
              const form = selectProcessDefinitionForm(state$.value);

              let errors: Array<ErrorInfo> = [];
              if (isValidationError(response)) {
                errors = getValidationErrors(i18n, response, form);
              }

              if (isPermissionError(response)) {
                errors = [getNotificationErrorProps(response.response, permissionNotificationErrorProps)];
              }

              return of(startProcessWithFormError(errors.length ? errors : [
                getNotificationErrorProps(response.response, tryAgainNotificationErrorProps),
              ]));
            }),
          );
      }),
    );
};

export const getFormByProcessDefinitionKeyEpic = (action$: ActionsObservable<Action>) => {
  return action$.pipe(
    filter(getProcessDefinitionFormRequest.match),
    mergeMap(({ payload }) => {
      return api.getProcessDefinitionByKey(payload).pipe(
        concatMap(({ response: processResponse }) => formApi
          .getForm(processResponse.formKey)
          .pipe(
            mergeMap(({ response: formResponse }) => of(
              getProcessDefinitionSuccess(processResponse as unknown as ProcessDefinition),
              getProcessDefinitionFormSuccess(formResponse),
            )),
            catchError((serverResponse) => of(getProcessDefinitionFormError(
              getCriticalErrorProps({ serverResponse, errorProps: processDefinitionFormNotFoundErrorProps }),
            ))),
          )),
        catchError((serverResponse) => {
          if (serverResponse.status === 404) {
            return of(getProcessDefinitionError(
              getCriticalErrorProps({ serverResponse, errorProps: processDefinitionNotFoundErrorProps }),
            ));
          }
          return of(getProcessDefinitionError(
            getCriticalErrorProps({
              serverResponse,
              options: { link: ROUTES.PROCESS_LIST, title: i18n.t('startFormPage:backLinkCaption') },
            }),
          ));
        }),
      );
    }),
  );
};
